import React from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { majorsGuideContent } from '@backrs_ui/grow-ui/src/pages/MajorsGuide/content';
import type {
  MajorCareerRelationshipV2,
  MajorCareerExample,
} from '@backrs_ui/grow-ui/src/pages/MajorsGuide/content/types';

import { Box, Typography, SxProps, Theme, useMediaQuery } from '@mui/material';

const content = majorsGuideContent.majors101;
const labels = majorsGuideContent.labels;
const relationships = content.relationshipsV2 ?? [];

export const Majors101InlineSection: React.FC = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <Box id="majors-101-inline-section" sx={containerSx}>
      <Box sx={keyDifferenceSectionSx}>
        <Box sx={heroImageContainerSx}>
          <Box
            component="img"
            src={
              isMobile ? '/images/career_paths_hero_mobile.svg' : '/images/career_paths_hero.svg'
            }
            alt="College Major vs Career Path comparison"
            sx={heroImageSx}
          />
        </Box>

        <Box sx={keyDifferenceTextSx}>
          <Box sx={keyDifferenceColumnSx}>
            <Typography variant="display-xs-semibold" sx={keyDifferenceTitleSx}>
              {content.keyDifference.title}
            </Typography>
            {content.keyDifference.paragraphs?.map((paragraph, idx) => (
              <Typography key={idx} variant="text-md-regular" sx={keyDifferenceParagraphSx}>
                {paragraph}
              </Typography>
            ))}
          </Box>
          <Box sx={keyDifferenceColumnSx}>
            <Typography variant="display-xs-semibold" sx={connectionTitleSx}>
              {content.keyDifference.connectionTitle}
            </Typography>
            <Typography variant="text-md-regular" sx={keyDifferenceParagraphSx}>
              {content.keyDifference.connectionDescription}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={relationshipsSectionSx}>
        <Box sx={categoryGridSx}>
          {relationships.map((rel: MajorCareerRelationshipV2, idx: number) => (
            <Box key={idx} sx={categoryColumnSx}>
              <Box sx={categoryTitleRowSx}>
                <AppIcon iconName={rel.icon ?? 'ArrowRight'} diameter="20px" color="Blue/500" />
                <Typography variant="text-md-semibold" sx={categoryTitleSx}>
                  {rel.type}
                </Typography>
              </Box>

              <Typography variant="text-sm-regular" sx={descriptionTextSx}>
                {rel.description}
              </Typography>

              <Typography variant="text-md-semibold">{labels.examples}</Typography>

              <Box sx={examplesColumnSx}>
                {rel.examples.map((example: MajorCareerExample, rowIdx: number) => (
                  <Box key={rowIdx} sx={exampleRowSx}>
                    <Typography variant="text-sm-medium" sx={majorTextSx}>
                      {example.major}
                    </Typography>
                    <AppIcon
                      iconName="ArrowRight"
                      diameter="20px"
                      color="Colors/Foreground/fg-brand-primary"
                    />
                    <Typography variant="text-sm-medium" sx={careerTextSx}>
                      {example.career}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const keyDifferenceSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const heroImageContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: backrs.colors['Gray/700'],
  borderRadius: backrs.radius['radius-xl'],
  p: backrs.spacing['spacing-xl'],
});

const heroImageSx: SxProps<Theme> = {
  maxWidth: '100%',
  height: 'auto',
  objectFit: 'contain',
};

const keyDifferenceTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const keyDifferenceTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 700px)': {
    flexDirection: 'row',
    gap: backrs.spacing['spacing-3xl'],
  },
});

const keyDifferenceColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 700px)': {
    flex: 1,
  },
});

const keyDifferenceParagraphSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const connectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const relationshipsSectionSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-4xl'],
});

const categoryGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@container (min-width: 1100px)': {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
});

const categoryColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const categoryTitleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const categoryTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Blue/700'],
});

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  flex: 1,
});

const examplesColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const exampleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const majorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const careerTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
